/**
 * Refine dataProvider — кастомный, под наш FastAPI /admin/* контракт.
 *
 * Контракт нашего API:
 *  - GET    /admin/{resource}                 → list (массив + X-Total-Count)
 *  - GET    /admin/{resource}/{id}            → one
 *  - POST   /admin/{resource}                 → create (отдаёт созданный объект)
 *  - POST   /admin/{resource}/{id}/{action}   → custom action
 *
 * Refine pagination: { current, pageSize } → query params { page, per_page }.
 * Refine sorters:    [{ field, order }]    → ?sort=field&order=asc|desc
 * Refine filters:    {[field]: value}      → ?{field}=value (whitelisted на бэке)
 *
 * Все запросы идут с credentials:"include" — для отправки JWT cookie.
 */
import type {
  CrudFilters,
  CrudSorting,
  DataProvider,
} from "@refinedev/core";

export const API_URL: string =
  import.meta.env.VITE_API_URL || "https://api.bemli.org";

const buildQuery = (
  params: Record<string, unknown>,
  sorters?: CrudSorting,
  filters?: CrudFilters
): URLSearchParams => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  }
  const s = sorters?.[0];
  if (s) {
    q.set("sort", s.field);
    q.set("order", s.order === "asc" ? "asc" : "desc");
  }
  for (const f of filters || []) {
    if ("field" in f && f.value !== undefined && f.value !== null && f.value !== "") {
      const key = f.field === "status" ? "status" : f.field;
      q.set(String(key), String(f.value));
    }
  }
  return q;
};

const fetchJSON = async (url: string, init?: RequestInit) => {
  const r = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    ...init,
  });
  if (!r.ok) {
    const detail = await r.text().catch(() => r.statusText);
    const err: any = new Error(`API ${r.status}: ${detail}`);
    err.statusCode = r.status;
    throw err;
  }
  const total = Number(r.headers.get("x-total-count") || 0);
  const body = r.status === 204 ? null : await r.json();
  return { body, total };
};

export const dataProvider: DataProvider = {
  getApiUrl: () => API_URL,

  async getList({ resource, pagination, sorters, filters }) {
    // У нас mixed-version Refine (@refinedev/core@5 + @refinedev/antd@6):
    // useTable@6 шлёт pagination с полем `currentPage`, а getList@5
    // интерфейс ожидает `current`. Берём оба, чтобы пагинация работала
    // независимо от того какая версия его пересылает.
    const pgAny = pagination as any;
    const page = pgAny?.current ?? pgAny?.currentPage ?? 1;
    const per_page = pgAny?.pageSize ?? 25;
    const q = buildQuery({ page, per_page }, sorters, filters);
    const { body, total: headerTotal } = await fetchJSON(
      `${API_URL}/admin/${resource}?${q.toString()}`
    );
    // Поддерживаем 3 формата body:
    //   1. Array          → raw array (legacy)
    //   2. {data, total}  → новый стандарт (users/subscriptions/payments/audit_log)
    //   3. {items, total} → старая обёртка (broadcasts/tags)
    let data: unknown[];
    let bodyTotal: number | undefined;
    if (Array.isArray(body)) {
      data = body;
    } else if (body && Array.isArray(body.data)) {
      data = body.data;
      bodyTotal = typeof body.total === "number" ? body.total : undefined;
    } else if (body && Array.isArray(body.items)) {
      data = body.items;
      bodyTotal = typeof body.total === "number" ? body.total : undefined;
    } else {
      data = [];
    }
    // Приоритет total: body.total → X-Total-Count → data.length.
    // body.total всегда доходит до клиента; header может срезаться CORS/CDN.
    const total =
      (bodyTotal !== undefined ? bodyTotal : 0) ||
      headerTotal ||
      data.length;
    return { data: data as any[], total };
  },

  async getOne({ resource, id }) {
    const { body } = await fetchJSON(`${API_URL}/admin/${resource}/${id}`);
    return { data: body };
  },

  async create({ resource, variables }) {
    const { body } = await fetchJSON(`${API_URL}/admin/${resource}`, {
      method: "POST",
      body: JSON.stringify(variables),
    });
    return { data: body };
  },

  async update({ resource, id, variables }) {
    const { body } = await fetchJSON(`${API_URL}/admin/${resource}/${id}`, {
      method: "PUT",
      body: JSON.stringify(variables),
    });
    return { data: body };
  },

  async deleteOne({ resource, id }) {
    const { body } = await fetchJSON(`${API_URL}/admin/${resource}/${id}`, {
      method: "DELETE",
    });
    return { data: body };
  },

  async custom(params: any) {
    const { url, method, query, headers } = params;
    // В Refine v5 `useCustomMutation` передаёт body в поле `values` (не
    // `payload` как было в v3/v4). Принимаем оба для совместимости —
    // без этого все POST через useCustomMutation шли пустым body и backend
    // отвечал 422 Unprocessable (поймали на /admin/subscriptions/<id>/extend).
    const body = params.values ?? params.payload;

    const u = new URL(url.startsWith("http") ? url : `${API_URL}${url}`);
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== null) u.searchParams.set(k, String(v));
      }
    }
    const init: RequestInit = { method: method || "GET", headers: headers as any };
    const m = init.method?.toUpperCase();
    if (body !== undefined && (m === "POST" || m === "PUT" || m === "PATCH")) {
      init.body = JSON.stringify(body);
    }
    const { body: respBody } = await fetchJSON(u.toString(), init);
    return { data: respBody };
  },
};
